import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/axios';
import {
  MdAccountBalance,
  MdRefresh,
  MdAdd,
  MdClose,
  MdDelete,
  MdCheckCircle,
  MdCreditCard,
  MdSavings,
  MdAttachMoney,
  MdEdit,
  MdHome,
} from 'react-icons/md';
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from 'recharts';
import { format } from 'date-fns';
import './Accounts.css';

const fmtMoney = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

const formatDateSince = (d) => {
  if (!d) return 'No activity';
  const date = new Date(d);
  const diffMs = Date.now() - date.getTime();
  const hours = Math.floor(diffMs / 36e5);
  if (hours < 1) return 'just now';
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  return format(date, 'MMM d, yyyy');
};

// Manual (non-Stripe) account types the user can add. Each maps to an
// asset/liability `kind` (drives the sign of net worth) and a display bucket.
const MANUAL_TYPES = [
  { value: 'retirement', label: '401k / Retirement', kind: 'asset', bucket: 'investments' },
  { value: 'investment', label: 'Investment / Brokerage', kind: 'asset', bucket: 'investments' },
  { value: 'real_estate', label: 'Real Estate', kind: 'asset', bucket: 'other' },
  { value: 'vehicle', label: 'Vehicle', kind: 'asset', bucket: 'other' },
  { value: 'other_asset', label: 'Other Asset', kind: 'asset', bucket: 'other' },
  { value: 'mortgage', label: 'Mortgage', kind: 'liability', bucket: 'loans' },
  { value: 'loan', label: 'Loan', kind: 'liability', bucket: 'loans' },
  { value: 'other_liability', label: 'Other Liability', kind: 'liability', bucket: 'loans' },
];
const MANUAL_TYPE_MAP = Object.fromEntries(MANUAL_TYPES.map((t) => [t.value, t]));

// Group buckets and how they map to the raw account_type from Stripe.
const GROUP_ORDER = ['cash', 'credit_cards', 'investments', 'loans', 'other'];
const GROUP_LABELS = {
  cash: 'Cash',
  credit_cards: 'Credit Cards',
  investments: 'Investments',
  loans: 'Loans',
  other: 'Other',
};
const GROUP_ICONS = {
  cash: MdSavings,
  credit_cards: MdCreditCard,
  investments: MdAttachMoney,
  loans: MdHome,
  other: MdAccountBalance,
};

const bucketFor = (account) => {
  if (account.is_manual) {
    return MANUAL_TYPE_MAP[account.account_type]?.bucket || 'other';
  }
  const t = (account.account_type || '').toLowerCase();
  if (t === 'credit_card' || t === 'credit') return 'credit_cards';
  if (t === 'checking' || t === 'savings' || t === 'cash' || t === 'depository') return 'cash';
  if (t === 'investment' || t === 'brokerage') return 'investments';
  return 'other';
};

// Returns the positive magnitude to display for an account.
// - Manual accounts store a signed balance (liabilities negative) → show abs.
// - Stripe: prefer real balance_amount; fall back to derived net_balance until
//   first sync lands. Credit-card balance is stored negative (owed) → show abs.
const displayBalance = (account) => {
  if (account.is_manual) {
    return Math.abs(parseFloat(account.balance_amount || 0));
  }
  const bucket = bucketFor(account);
  const real = account.balance_amount;
  if (real !== null && real !== undefined) {
    const n = parseFloat(real);
    return bucket === 'credit_cards' ? Math.abs(Math.min(n, 0)) : Math.max(n, 0);
  }
  const net = parseFloat(account.net_balance || 0);
  if (bucket === 'credit_cards') return Math.abs(Math.min(net, 0));
  return Math.max(net, 0);
};

const isLiability = (account) => {
  if (account.is_manual) return account.kind === 'liability';
  return bucketFor(account) === 'credit_cards';
};

// Liquid vs non-liquid classification for the net-worth breakdown.
//   Liquid     = all Stripe accounts (cash, credit cards, brokerage) plus
//                manual Investment/Brokerage and manual "Other" types.
//   Non-liquid = manual 401k/retirement, real estate, vehicle, mortgage, loan.
const NON_LIQUID_MANUAL = new Set(['retirement', 'real_estate', 'vehicle', 'mortgage', 'loan']);
const isLiquid = (account) => {
  if (account.is_manual) return !NON_LIQUID_MANUAL.has(account.account_type);
  return true; // every Stripe-linked account is liquid
};

// Signed contribution to net worth: assets add, liabilities subtract.
const signedBalance = (account) =>
  isLiability(account) ? -displayBalance(account) : displayBalance(account);

const initials = (name) => {
  if (!name) return '?';
  const parts = name.replace(/[^A-Za-z ]/g, '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
};

const SyncedModal = ({ accounts, onClose }) => (
  <div className="modal-backdrop" onClick={onClose}>
    <div className="modal-card" onClick={(e) => e.stopPropagation()}>
      <button className="modal-close" onClick={onClose}><MdClose /></button>
      <div className="modal-check"><MdCheckCircle /></div>
      <h3 className="modal-title">
        Your {accounts[0]?.institution_name || 'bank'} accounts have synced
      </h3>
      <div className="modal-account-list">
        {accounts.map((a) => (
          <div key={a.id} className="modal-account-row">
            <div className="modal-account-avatar">{initials(a.institution_name)}</div>
            <div className="modal-account-info">
              <div className="modal-account-name">{a.account_name}</div>
              <div className="modal-account-type">{a.account_type?.replace(/_/g, ' ')}</div>
            </div>
            <div className="modal-account-bal">{fmtMoney(displayBalance(a))}</div>
          </div>
        ))}
      </div>
      <div className="modal-actions">
        <button className="button" onClick={onClose}>Done</button>
      </div>
    </div>
  </div>
);

// Modal for adding a manual account (401k, mortgage, etc.). Type selection
// drives whether it's an asset or liability; the user always enters a positive
// balance and the backend applies the sign.
const ManualAccountModal = ({ onClose, onSave }) => {
  const [name, setName] = useState('');
  const [type, setType] = useState('retirement');
  const [balance, setBalance] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const meta = MANUAL_TYPE_MAP[type];

  const submit = async () => {
    const amt = parseFloat(balance);
    if (!name.trim()) return setError('Enter an account name');
    if (!Number.isFinite(amt) || amt < 0) return setError('Enter a valid balance');
    setSaving(true);
    setError('');
    try {
      await onSave({ account_name: name.trim(), account_type: type, kind: meta.kind, balance: amt });
      onClose();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to add account');
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}><MdClose /></button>
        <h3 className="modal-title">Add manual account</h3>
        <p className="manual-modal-sub">
          For 401k, mortgage, real estate — anything not linked through your bank.
          You update the balance yourself whenever you check a statement.
        </p>

        <label className="manual-field">
          <span>Account name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Fidelity 401k"
            autoFocus
          />
        </label>

        <label className="manual-field">
          <span>Type</span>
          <select value={type} onChange={(e) => setType(e.target.value)}>
            <optgroup label="Assets">
              {MANUAL_TYPES.filter((t) => t.kind === 'asset').map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </optgroup>
            <optgroup label="Liabilities">
              {MANUAL_TYPES.filter((t) => t.kind === 'liability').map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </optgroup>
          </select>
        </label>

        <label className="manual-field">
          <span>{meta.kind === 'liability' ? 'Amount owed' : 'Current balance'}</span>
          <input
            type="number"
            min="0"
            step="0.01"
            value={balance}
            onChange={(e) => setBalance(e.target.value)}
            placeholder="0.00"
          />
          {meta.kind === 'liability' && (
            <small className="manual-hint">
              Enter what you owe as a positive number — it counts against your net worth.
            </small>
          )}
        </label>

        {error && <div className="error">{error}</div>}

        <div className="modal-actions">
          <button className="button button-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="button" onClick={submit} disabled={saving}>
            {saving ? 'Saving…' : 'Add account'}
          </button>
        </div>
      </div>
    </div>
  );
};

const Accounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [busy, setBusy] = useState(false);
  const [syncedAccounts, setSyncedAccounts] = useState(null);
  const [collapsed, setCollapsed] = useState({});
  const [showManualModal, setShowManualModal] = useState(false);

  const fetchAccounts = useCallback(async () => {
    try {
      const [acctRes, histRes] = await Promise.all([
        api.get('/api/accounts'),
        api.get('/api/accounts/net-worth-history'),
      ]);
      setAccounts(acctRes.data);
      setHistory(histRes.data);
    } catch (error) {
      console.error('Error fetching accounts:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const ensureStripe = () =>
    new Promise((resolve, reject) => {
      if (window.Stripe) return resolve();
      const existing = document.querySelector('script[data-stripe-loader]');
      if (existing) {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', () => reject(new Error('Stripe.js failed to load')));
        return;
      }
      const s = document.createElement('script');
      s.src = 'https://js.stripe.com/v3/';
      s.async = true;
      s.dataset.stripeLoader = '1';
      s.onload = () => resolve();
      s.onerror = () => reject(new Error('Stripe.js failed to load'));
      document.head.appendChild(s);
    });

  const handleConnectBank = async () => {
    setBusy(true);
    setMessage({ type: '', text: '' });
    try {
      await ensureStripe();
      const response = await api.post('/api/stripe/connect');
      const { client_secret } = response.data;

      const stripe = window.Stripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
      if (!stripe) throw new Error('Stripe not loaded — add REACT_APP_STRIPE_PUBLISHABLE_KEY');

      const result = await stripe.collectFinancialConnectionsAccounts({ clientSecret: client_secret });

      if (result.error) {
        setMessage({ type: 'error', text: `Connection failed: ${result.error.message}` });
        return;
      }

      const connected = result.financialConnectionsSession.accounts;
      await api.post('/api/accounts', { accounts: connected });

      // Wait for accounts to land in DB, then sync transactions and show modal.
      setTimeout(async () => {
        try {
          await api.post('/api/stripe/sync-transactions');
        } catch (e) {
          console.error('Sync after connect failed:', e);
        }
        await fetchAccounts();
        const fresh = (await api.get('/api/accounts')).data;
        const ids = new Set(connected.map((c) => c.id));
        setSyncedAccounts(fresh.filter((a) => ids.has(a.stripe_account_id)));
      }, 2000);
    } catch (error) {
      console.error('Error connecting bank:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.error || error.message || 'Failed to connect bank',
      });
    } finally {
      setBusy(false);
    }
  };

  const handleSync = async () => {
    setBusy(true);
    setMessage({ type: '', text: '' });
    try {
      const response = await api.post('/api/stripe/sync-transactions');
      setMessage({
        type: 'success',
        text: response.data.message || `Synced ${response.data.synced} transactions`,
      });
      await fetchAccounts();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to sync transactions',
      });
    } finally {
      setBusy(false);
    }
  };

  const handleAddManual = async (payload) => {
    // Throws on failure so the modal can show the error inline.
    await api.post('/api/accounts/manual', payload);
    await fetchAccounts();
    setMessage({ type: 'success', text: `Added ${payload.account_name}` });
  };

  const handleUpdateBalance = async (account) => {
    const current = Math.abs(parseFloat(account.balance_amount || 0));
    const input = window.prompt(
      `New ${account.kind === 'liability' ? 'amount owed' : 'balance'} for ${account.account_name}:`,
      current
    );
    if (input === null) return; // cancelled
    const amt = parseFloat(input);
    if (!Number.isFinite(amt) || amt < 0) {
      setMessage({ type: 'error', text: 'Enter a valid non-negative number' });
      return;
    }
    try {
      await api.put(`/api/accounts/${account.id}/balance`, { balance: amt });
      setMessage({ type: 'success', text: `Updated ${account.account_name}` });
      await fetchAccounts();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to update balance',
      });
    }
  };

  const handleRemove = async (account) => {
    const label = account.is_manual
      ? account.account_name
      : `${account.institution_name} — ${account.account_name}`;
    const warning = account.is_manual
      ? ''
      : '\nAll transactions for this account will also be deleted.';
    const confirmed = window.confirm(`Remove ${label}?${warning}`);
    if (!confirmed) return;

    try {
      await api.delete(`/api/accounts/${account.id}`);
      setMessage({ type: 'success', text: 'Account removed' });
      await fetchAccounts();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to remove account',
      });
    }
  };

  if (loading) return <div className="loading">Loading accounts...</div>;

  const grouped = GROUP_ORDER.reduce((acc, key) => ({ ...acc, [key]: [] }), {});
  accounts.forEach((a) => grouped[bucketFor(a)].push(a));

  const assets = accounts
    .filter((a) => !isLiability(a))
    .reduce((s, a) => s + displayBalance(a), 0);
  const liabilities = accounts
    .filter((a) => isLiability(a))
    .reduce((s, a) => s + displayBalance(a), 0);

  // Liquid / non-liquid breakdown. signedBalance already subtracts liabilities,
  // so each subtotal is a true net figure. liquid + non-liquid === total.
  const liquidNetWorth = accounts
    .filter(isLiquid)
    .reduce((s, a) => s + signedBalance(a), 0);
  const nonLiquidNetWorth = accounts
    .filter((a) => !isLiquid(a))
    .reduce((s, a) => s + signedBalance(a), 0);
  const netWorth = liquidNetWorth + nonLiquidNetWorth;

  // Build chart data from real snapshot history. If empty, fall back to a single
  // point at today so the chart still renders something useful.
  const chartData = history.length > 0
    ? history.map((p) => ({ date: format(new Date(p.date), 'MMM d'), value: parseFloat(p.net_worth) }))
    : [{ date: format(new Date(), 'MMM d'), value: netWorth }];

  return (
    <div className="accounts-page">
      {syncedAccounts && (
        <SyncedModal
          accounts={syncedAccounts}
          onClose={() => setSyncedAccounts(null)}
        />
      )}

      {showManualModal && (
        <ManualAccountModal
          onClose={() => setShowManualModal(false)}
          onSave={handleAddManual}
        />
      )}

      {message.text && (
        <div className={message.type}>{message.text}</div>
      )}

      <div className="card accounts-top">
        <div className="accounts-top-left">
          <div className="accounts-net-label">NET WORTH</div>
          <div className="accounts-net-value">{fmtMoney(netWorth)}</div>
          <div className="accounts-net-breakdown">
            <div className="net-chip">
              <span className="net-chip-label">Liquid</span>
              <span className="net-chip-value">{fmtMoney(liquidNetWorth)}</span>
            </div>
            <div className="net-chip">
              <span className="net-chip-label">Non-liquid</span>
              <span className="net-chip-value">{fmtMoney(nonLiquidNetWorth)}</span>
            </div>
          </div>
          <div className="accounts-net-sub">
            {accounts.length} account{accounts.length === 1 ? '' : 's'} connected
          </div>
        </div>
        <div className="accounts-top-actions">
          <button className="button button-secondary" onClick={handleSync} disabled={busy}>
            <MdRefresh /> {busy ? 'Syncing…' : 'Sync'}
          </button>
          <button className="button button-secondary" onClick={() => setShowManualModal(true)} disabled={busy}>
            <MdAdd /> Add manual
          </button>
          <button className="button" onClick={handleConnectBank} disabled={busy}>
            <MdAdd /> Add account
          </button>
        </div>
      </div>

      <div className="accounts-body">
        <div className="accounts-left">
          {accounts.length === 0 ? (
            <div className="card">
              <p className="empty-state">
                No accounts connected yet. Click <strong>+ Add account</strong> to link a bank via Stripe.
              </p>
            </div>
          ) : (
            <>
              <div className="card chart-card">
                <ResponsiveContainer width="100%" height={180}>
                  <LineChart data={chartData}>
                    <CartesianGrid stroke="#eee" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#9a9a96' }} tickLine={false} axisLine={false} />
                    <YAxis tickFormatter={fmtMoney} tick={{ fontSize: 11, fill: '#9a9a96' }} tickLine={false} axisLine={false} />
                    <Tooltip formatter={(v) => fmtMoney(v)} />
                    <Line type="monotone" dataKey="value" stroke="#2f6df6" strokeWidth={2} dot />
                  </LineChart>
                </ResponsiveContainer>
                <div className="chart-note">
                  {history.length > 0
                    ? `${history.length} snapshot${history.length === 1 ? '' : 's'} — each Sync adds a new point.`
                    : 'Click Sync to record your first net-worth snapshot.'}
                </div>
              </div>

              {GROUP_ORDER.filter((g) => grouped[g].length > 0).map((groupKey) => {
                const group = grouped[groupKey];
                const Icon = GROUP_ICONS[groupKey];
                const groupTotal = group.reduce((s, a) => s + displayBalance(a), 0);
                const isCollapsed = collapsed[groupKey];
                return (
                  <div key={groupKey} className="card account-group">
                    <button
                      className="account-group-header"
                      onClick={() => setCollapsed({ ...collapsed, [groupKey]: !isCollapsed })}
                    >
                      <span className="account-group-title">
                        <Icon className="account-group-icon" />
                        {GROUP_LABELS[groupKey]}
                      </span>
                      <span className="account-group-total">{fmtMoney(groupTotal)}</span>
                    </button>
                    {!isCollapsed && (
                      <div className="account-list">
                        {group.map((a) => (
                          <div key={a.id} className="account-row">
                            <div className="account-avatar">
                              {initials(a.is_manual ? a.account_name : a.institution_name)}
                            </div>
                            <div className="account-main">
                              <div className="account-name">
                                {a.account_name}
                                {a.is_manual && <span className="account-manual-tag">Manual</span>}
                              </div>
                              <div className="account-meta">
                                {a.is_manual
                                  ? MANUAL_TYPE_MAP[a.account_type]?.label || 'Manual'
                                  : (a.account_subtype || a.account_type || '').replace(/_/g, ' ')}
                                {(a.balance_as_of || a.last_transaction_date) && (
                                  <> · {formatDateSince(a.balance_as_of || a.last_transaction_date)}</>
                                )}
                              </div>
                            </div>
                            <div className="account-right">
                              <div className={`account-balance ${isLiability(a) ? 'liability' : ''}`}>
                                {fmtMoney(displayBalance(a))}
                              </div>
                              {a.is_manual && (
                                <button
                                  className="icon-button"
                                  onClick={() => handleUpdateBalance(a)}
                                  title="Update balance"
                                >
                                  <MdEdit />
                                </button>
                              )}
                              <button
                                className="icon-button account-remove"
                                onClick={() => handleRemove(a)}
                                title="Remove account"
                              >
                                <MdDelete />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>

        {accounts.length > 0 && (
          <aside className="accounts-summary card">
            <h3 className="summary-title">Summary</h3>
            <div className="summary-row">
              <span className="summary-label">Assets</span>
              <span className="summary-value">{fmtMoney(assets)}</span>
            </div>
            <div className="summary-bar"><div className="summary-bar-fill assets" style={{ width: '100%' }}></div></div>

            <div className="summary-row">
              <span className="summary-label">Liabilities</span>
              <span className="summary-value">{fmtMoney(liabilities)}</span>
            </div>
            <div className="summary-bar">
              <div
                className="summary-bar-fill liabilities"
                style={{ width: `${assets > 0 ? Math.min(100, (liabilities / assets) * 100) : 0}%` }}
              ></div>
            </div>

            <div className="summary-row total">
              <span className="summary-label">Net worth</span>
              <span className={`summary-value ${netWorth < 0 ? 'neg' : ''}`}>{fmtMoney(netWorth)}</span>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
};

export default Accounts;
